import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ChevronRight, Info } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { EstimateWidget } from "../components/EstimateWidget";
import { ChatWidget } from "../components/ChatWidget";
import { useEstimate } from "../context/EstimateContext";
import { useLenis } from "../hooks/useLenis";
import { COMPANY } from "../data/site";

const META_TITLE = "Home Remodeling Cost Guide — Lehigh Valley, PA (2026) | Apex Home Pro";
const META_DESC =
  "How much do remodeling projects cost in the Lehigh Valley, PA? 2026 price ranges for kitchens, bathrooms, basements, additions, decks, fencing, flooring and concrete. Free estimates from Apex Home Pro Inc.";

const ROWS = [
  { name: "Kitchen Remodel", slug: "kitchens-baths", range: "$20,000 – $75,000+", note: "Refresh to full gut with new cabinets & counters" },
  { name: "Bathroom Remodel", slug: "bathrooms", range: "$10,000 – $35,000", note: "Tub-to-shower, tile, vanity & fixtures" },
  { name: "Basement Finishing", slug: "basement-finishing", range: "$30,000 – $70,000", note: "Family room, bath, egress & finishes" },
  { name: "Home Addition", slug: "additions", range: "$40,000 – $150,000+", note: "Room, second story or in-law suite" },
  { name: "Deck (composite/wood)", slug: "decks", range: "$8,000 – $30,000", note: "Size, material & railings drive cost" },
  { name: "Vinyl / Wood Fencing", slug: "fencing", range: "$3,500 – $12,000", note: "Per typical yard; varies by linear feet" },
  { name: "Flooring (per room/home)", slug: "flooring", range: "$3,000 – $20,000", note: "Hardwood, LVP or tile" },
  { name: "Windows & Doors", slug: "windows-doors", range: "$650 – $1,500 / unit", note: "Installed, per window or door" },
  { name: "Concrete (patio/walkway)", slug: "concrete", range: "$6 – $15 / sq ft", note: "Broom, smooth or stamped finish" },
  { name: "Interior/Exterior Painting", slug: "paint", range: "$2,500 – $9,000", note: "Room count & prep dependent" },
];

const FAQS = [
  { q: "Why do remodeling costs vary so much?", a: "Cost depends on size, materials, finishes, structural work and site conditions. The ranges here are typical Lehigh Valley estimates — your free on-site estimate gives exact numbers." },
  { q: "Do you offer free estimates?", a: "Yes. Every estimate is free and comes as a detailed, itemized written quote with no obligation." },
  { q: "Can you work with my budget?", a: "Absolutely. We offer options at different price points and give honest guidance on where to spend and where to save." },
];

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function CostGuide() {
  const { openEstimate } = useEstimate();
  useLenis();

  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : "";
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    const faq = document.createElement("script");
    faq.type = "application/ld+json";
    faq.id = "costguide-faq-jsonld";
    faq.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(faq);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      setMeta("description", prevDesc);
      const s = document.getElementById("costguide-faq-jsonld");
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="bg-[var(--apex-bg)] min-h-screen">
      <Header />
      <main className="pt-[84px]">
        <section className="bg-[var(--apex-primary)] text-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
            <nav className="flex items-center gap-1.5 text-sm text-slate-300 mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-white">Cost Guide</span>
            </nav>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--apex-accent)]">Pricing transparency</p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.03]"
            >
              Lehigh Valley Remodeling Cost Guide
            </motion.h1>
            <p className="mt-5 text-slate-300 max-w-2xl text-lg">
              Real, typical price ranges for home projects across {COMPANY.area} — so you can plan with confidence. Every project is unique; your free estimate gives exact numbers.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
          <div className="overflow-hidden rounded-md border border-slate-200 shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-[var(--apex-surface)] text-[var(--apex-primary)]">
                <tr>
                  <th className="px-4 sm:px-6 py-4 font-display font-bold">Project</th>
                  <th className="px-4 sm:px-6 py-4 font-display font-bold whitespace-nowrap">Typical Range</th>
                  <th className="px-4 sm:px-6 py-4 font-display font-bold hidden sm:table-cell">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {ROWS.map((r) => (
                  <tr key={r.slug} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4">
                      <Link to={`/services/${r.slug}`} className="font-semibold text-[var(--apex-primary)] hover:text-[var(--apex-accent)] transition-colors">{r.name}</Link>
                    </td>
                    <td className="px-4 sm:px-6 py-4 font-bold text-[var(--apex-accent)] whitespace-nowrap">{r.range}</td>
                    <td className="px-4 sm:px-6 py-4 text-slate-600 text-sm hidden sm:table-cell">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 flex items-start gap-2 text-sm text-slate-500">
            <Info size={16} className="mt-0.5 shrink-0" />
            Ranges are general 2026 estimates for the Lehigh Valley and can vary with materials, size and site conditions. They are not a quote.
          </p>

          <div className="mt-10 rounded-md bg-[var(--apex-primary)] p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-display font-bold text-2xl text-white">Want an exact number?</h2>
              <p className="text-slate-300 mt-1">Get a free, itemized estimate for your specific project — no obligation.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button data-testid="costguide-estimate-btn" onClick={openEstimate} className="inline-flex items-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold px-6 py-3.5 transition-colors">
                Free Estimate <ArrowRight size={18} />
              </button>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 rounded-sm border border-white/20 hover:border-white/50 text-white font-semibold px-6 py-3.5 transition-colors">
                <Phone size={18} />
              </a>
            </div>
          </div>

          <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-14">Frequently asked</h2>
          <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f) => (
              <div key={f.q} className="py-5">
                <h3 className="font-semibold text-[var(--apex-primary)]">{f.q}</h3>
                <p className="text-slate-600 mt-2">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <EstimateWidget />
      <ChatWidget />
    </div>
  );
}
